import React, { useState } from "react";
import { X, Flag, ShieldOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { id: "harassment", label: "Harassment or threats" },
  { id: "inappropriate_content", label: "Inappropriate photos or content" },
  { id: "fake_profile", label: "Fake profile or impersonation" },
  { id: "spam", label: "Spam or scam" },
  { id: "underage", label: "Suspected to be under 18" },
  { id: "other", label: "Something else" },
];

export default function SafetyModal({ targetId, targetName, threadId, onClose, onBlocked }) {
  const [view, setView] = useState("menu"); // menu | report
  const [reason, setReason] = useState("");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);

  const block = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${targetId}/block`);
      toast.success(`${targetName || "User"} blocked.`);
      onBlocked?.();
      onClose();
    } catch {
      toast.error("Could not block");
    } finally {
      setBusy(false);
    }
  };

  const submitReport = async () => {
    if (!reason) {
      toast.error("Pick a reason");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/users/${targetId}/report`, {
        reason,
        context: context.trim() || undefined,
        thread_id: threadId,
      });
      toast.success("Report submitted. They've been blocked.");
      onBlocked?.();
      onClose();
    } catch {
      toast.error("Could not submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center max-w-md mx-auto"
      onClick={onClose}
      data-testid="safety-modal"
    >
      <div
        className="w-full bg-[#1B1B1E] rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-medium tracking-tight">
            {view === "menu" ? "Safety" : "Report"}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#25252A] flex items-center justify-center active:scale-90"
            data-testid="safety-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {view === "menu" && (
          <div className="space-y-3">
            <p className="text-sm text-[#A0A0A8]">
              You're in control. Spark takes safety seriously — we review every report.
            </p>
            <button
              onClick={() => setView("report")}
              className="w-full bg-[#25252A] text-[#F0E6D2] rounded-2xl p-4 flex items-center gap-3 active:scale-[0.99]"
              data-testid="safety-report-open"
            >
              <Flag className="w-5 h-5 text-[#B95F73]" />
              <div className="text-left">
                <div className="font-bold text-sm">Report {targetName || "this user"}</div>
                <div className="text-xs text-[#A0A0A8]">For harassment, fake profile, or more</div>
              </div>
            </button>
            <button
              onClick={block}
              disabled={busy}
              className="w-full bg-[#25252A] text-[#F0E6D2] rounded-2xl p-4 flex items-center gap-3 active:scale-[0.99] disabled:opacity-60"
              data-testid="safety-block"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldOff className="w-5 h-5 text-[#B95F73]" />}
              <div className="text-left">
                <div className="font-bold text-sm">Block {targetName || "this user"}</div>
                <div className="text-xs text-[#A0A0A8]">They won't be able to message or see you</div>
              </div>
            </button>
          </div>
        )}

        {view === "report" && (
          <div className="space-y-3">
            <p className="text-sm text-[#A0A0A8] mb-2">What's wrong?</p>
            {REASONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setReason(r.id)}
                data-testid={`reason-${r.id}`}
                className={`w-full text-left rounded-2xl px-4 py-3 text-sm font-semibold border transition active:scale-[0.99] ${
                  reason === r.id
                    ? "bg-[#B95F73] text-[#F0E6D2] border-[#B95F73]"
                    : "bg-[#25252A] text-[#F0E6D2] border-[#2A2A2F]"
                }`}
              >
                {r.label}
              </button>
            ))}
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Anything else we should know? (optional)"
              rows={3}
              maxLength={500}
              data-testid="report-context"
              className="w-full bg-[#25252A] border border-[#2A2A2F] rounded-2xl px-4 py-3 outline-none focus:border-[#B95F73] text-sm text-[#F0E6D2] mt-2"
            />
            <button
              onClick={submitReport}
              disabled={busy || !reason}
              className="w-full bg-[#B95F73] text-[#F0E6D2] rounded-full py-3.5 font-bold active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
              data-testid="report-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
              Submit report
            </button>
            <button
              onClick={() => setView("menu")}
              className="w-full text-[#A0A0A8] text-xs font-bold uppercase tracking-widest py-2"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
